import React, { useState } from 'react';
import { Section } from './ui/Section';
import { Mic, Radio, Users, MessageSquare, ChevronRight } from 'lucide-react';
import { speakingEngagements } from '../content/speaking';
const typeConfig = {
  talk: {
    icon: Mic,
    label: 'Talk',
    color: 'from-blue-500/20 to-cyan-500/20',
    badge: 'bg-blue-500/20 text-blue-300 border-blue-500/30'
  },
  podcast: {
    icon: Radio,
    label: 'Podcast',
    color: 'from-purple-500/20 to-pink-500/20',
    badge: 'bg-purple-500/20 text-purple-300 border-purple-500/30'
  },
  workshop: {
    icon: Users,
    label: 'Workshop',
    color: 'from-emerald-500/20 to-teal-500/20',
    badge: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30'
  },
  panel: {
    icon: MessageSquare,
    label: 'Panel',
    color: 'from-orange-500/20 to-rose-500/20',
    badge: 'bg-orange-500/20 text-orange-300 border-orange-500/30'
  }
};
const isUpcoming = (dateStr: string) => {
  const date = new Date(dateStr);
  const now = new Date();
  return date > now;
};
interface SpeakingProps {
  onNavigate: (slug: string) => void;
}
export function Speaking({
  onNavigate
}: SpeakingProps) {
  const [pastShownCount, setPastShownCount] = useState(3);
  // Split engagements into upcoming and past
  const upcomingEngagements = speakingEngagements.filter(e => isUpcoming(e.date));
  const pastEngagements = speakingEngagements.filter(e => !isUpcoming(e.date));
  const displayedPastEngagements = pastEngagements.slice(0, pastShownCount);
  const hasMorePast = pastShownCount < pastEngagements.length;
  const renderEngagementCard = (engagement: (typeof speakingEngagements)[0], index: number) => {
    const config = typeConfig[engagement.type];
    const Icon = config.icon;
    const upcoming = isUpcoming(engagement.date);
    return <button key={index} onClick={() => onNavigate(engagement.slug)} className="group relative overflow-hidden rounded-2xl border border-border/50 dark:border-white/10 bg-card/80 dark:bg-card/30 backdrop-blur-sm hover:border-border dark:hover:border-white/20 transition-all duration-300 p-6 shadow-sm hover:shadow-md text-left w-full" data-id="element-82">
        {/* Background Gradient */}
        <div className={`absolute inset-0 bg-gradient-to-br ${config.color} opacity-0 group-hover:opacity-100 transition-opacity duration-500`} data-id="element-83" />

        <div className="relative z-10 space-y-4" data-id="element-84">
          <div className="flex items-start justify-between" data-id="element-85">
            <div className="p-2 rounded-lg bg-secondary/50 dark:bg-white/5 border border-border/50 dark:border-white/10 text-foreground" data-id="element-86">
              <Icon size={20} data-id="element-87" />
            </div>
            <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-foreground group-hover:translate-x-1 transition-all" data-id="element-88" />
          </div>

          <div data-id="element-89">
            <div className="flex flex-wrap gap-2 mb-3" data-id="element-90">
              <span className={`inline-block text-xs px-2 py-1 rounded-md border ${config.badge}`} data-id="element-91">
                {config.label}
              </span>
              {upcoming && <span className="inline-block text-xs px-2 py-1 rounded-md border bg-emerald-500/20 text-emerald-300 border-emerald-500/30" data-id="element-92">
                  Upcoming
                </span>}
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2 group-hover:text-blue-400 transition-colors" data-id="element-93">
              {engagement.title}
            </h3>
            <p className="text-sm font-mono text-muted-foreground mb-1" data-id="element-94">
              {engagement.event}
            </p>
            <p className="text-xs text-muted-foreground" data-id="element-95">{engagement.date}</p>
          </div>

          {engagement.description && <p className="text-sm text-muted-foreground leading-relaxed line-clamp-2" data-id="element-96">
              {engagement.description}
            </p>}
        </div>
      </button>;
  };
  return <Section id="speaking" className="py-24 border-t border-border/20" data-id="element-97">
      <div className="mb-12" data-id="element-98">
        <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4" data-id="element-99">
          Speaking & Collaborations
        </h2>
        <p className="text-xl text-muted-foreground max-w-2xl" data-id="element-100">
          Sharing insights on engineering leadership, team scaling, and AI
          adoption at conferences and podcasts.
        </p>
      </div>

      {/* Upcoming Section */}
      {upcomingEngagements.length > 0 && <div className="mb-16" data-id="element-101">
          <h3 className="text-2xl font-semibold mb-6 text-foreground" data-id="element-102">
            Upcoming
          </h3>

          {/* Desktop: Grid layout */}
          <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-103">
            {upcomingEngagements.map((engagement, index) => renderEngagementCard(engagement, index))}
          </div>

          {/* Mobile: Horizontal scroll */}
          <div className="md:hidden overflow-x-auto pb-6 -mx-6 px-6" data-id="element-104">
            <div className="flex gap-4" style={{
          width: 'max-content'
        }} data-id="element-105">
              {upcomingEngagements.map((engagement, index) => <div key={index} style={{
            width: '300px',
            flexShrink: 0
          }} data-id="element-106">
                  {renderEngagementCard(engagement, index)}
                </div>)}
            </div>
          </div>
        </div>}

      {/* Past Section */}
      {pastEngagements.length > 0 && <div data-id="element-107">
          <h3 className="text-2xl font-semibold mb-6 text-foreground" data-id="element-108">Past</h3>

          {/* Desktop: Grid layout */}
          <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8" data-id="element-109">
            {displayedPastEngagements.map((engagement, index) => renderEngagementCard(engagement, index))}
          </div>

          {/* Mobile: Horizontal scroll */}
          <div className="md:hidden overflow-x-auto pb-6 -mx-6 px-6 mb-8" data-id="element-110">
            <div className="flex gap-4" style={{
          width: 'max-content'
        }} data-id="element-111">
              {displayedPastEngagements.map((engagement, index) => <div key={index} style={{
            width: '300px',
            flexShrink: 0
          }} data-id="element-112">
                  {renderEngagementCard(engagement, index)}
                </div>)}
            </div>
          </div>

          {/* Show More Button */}
          {hasMorePast && <div className="flex justify-center" data-id="element-113">
              <button onClick={() => setPastShownCount(prev => prev + 3)} className="group flex items-center gap-2 px-6 py-3 rounded-xl bg-secondary/50 dark:bg-white/5 border border-border/50 dark:border-white/10 hover:bg-secondary dark:hover:bg-white/10 hover:border-border dark:hover:border-white/20 transition-all duration-300 text-foreground font-medium shadow-sm" data-id="element-114">
                Show more
                <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" data-id="element-115" />
              </button>
            </div>}
        </div>}
    </Section>;
}